// Reference images (finishing bundle 2B, C11). Pure, framework-free helpers
// shared by the worker (upload limits, client gating, suggestion merge, round
// summary) and the browser (editor, client page, PDF).
//
// Storage (plan P-c as amended): the existing recordings R2 bucket, under
// `images/{scriptId}/{imageId}.jpg`. The browser resizes and re-encodes every
// image to JPEG before upload (react-pdf can't reliably decode WebP).

/** ProseMirror node name of a reference image. */
export const REF_IMAGE_NODE = 'refImage';

/** Images one script's working copy may reference. */
export const MAX_IMAGES_PER_SCRIPT = 30;

/** Hard ceiling per stored image, after the browser's resize. */
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/** The browser aims for this; the worker only enforces MAX_IMAGE_BYTES. */
export const TARGET_IMAGE_BYTES = 500 * 1024;

/** Longest stored edge, in pixels. */
export const MAX_IMAGE_EDGE = 1600;

/** One field is both the caption and the alt text. */
export const MAX_CAPTION_CHARS = 200;

/** The only stored type. */
export const STORED_IMAGE_MIME = 'image/jpeg';

const IMAGE_ID = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/** Image ids are ULIDs (src/shared/ulid.ts). */
export function isImageId(value: unknown): value is string {
  return typeof value === 'string' && IMAGE_ID.test(value);
}

interface JsonNode {
  type?: unknown;
  attrs?: Record<string, unknown> | null;
  content?: unknown;
}

function isNode(value: unknown): value is JsonNode {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function children(node: JsonNode): unknown[] {
  return Array.isArray(node.content) ? node.content : [];
}

/** Every refImage node in document order (tolerant of junk input). */
function refImageNodes(doc: unknown): JsonNode[] {
  const out: JsonNode[] = [];
  const walk = (node: unknown, depth: number) => {
    if (!isNode(node) || depth > 64) return;
    if (node.type === REF_IMAGE_NODE) {
      out.push(node);
      return;
    }
    for (const child of children(node)) walk(child, depth + 1);
  };
  walk(doc, 0);
  return out;
}

function imageIdOf(node: JsonNode): string | null {
  const id = node.attrs?.imageId;
  return isImageId(id) ? id : null;
}

/** Unique image ids in document order. */
export function collectRefImageIds(doc: unknown): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const node of refImageNodes(doc)) {
    const id = imageIdOf(node);
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/** Number of refImage nodes (the 30-per-script limit counts these). */
export function countRefImages(doc: unknown): number {
  return refImageNodes(doc).length;
}

/** Persistence backstop for every route, restore, and direct database writer.
 * Unreadable JSON remains the schema validator's responsibility. */
export function assertRefImageLimitJson(docJson: string): void {
  let doc: unknown;
  try { doc = JSON.parse(docJson); } catch { return; }
  if (countRefImages(doc) > MAX_IMAGES_PER_SCRIPT) throw new Error('This script can hold at most 30 images.');
}

/** Strip misplaced atoms before autosave validation, preserving all other content. */
export function stripMisplacedImages(doc: unknown): unknown {
  const clean = (node: unknown, depth = 0): unknown => {
    if (!isNode(node) || depth > 64) return node;
    const allowed = node.type === 'scriptCell' && node.attrs?.kind === 'visuals';
    if (!Array.isArray(node.content)) return node;
    return { ...node, content: node.content.flatMap((child) =>
      isNode(child) && child.type === REF_IMAGE_NODE
        ? (allowed ? [child] : [])
        : [clean(child, depth + 1)],
    ) };
  };
  return clean(doc);
}

/** Suggestions own row text, while the master owns each surviving row's images.
 * Rows have no persistent ids in the current schema. Match unchanged row text
 * first (including moves), then align edited rows in document order. Scope the
 * matching to each brief field so pictures cannot cross audience boundaries.
 */
export function sanitizeSuggestionImages(masterDoc: unknown, suggestionDoc: unknown): unknown {
  type Row = { node: JsonNode; scope: string; key: string };
  const rows = (doc: unknown): Row[] => {
    const result: Row[] = [];
    const text = (node: unknown, depth = 0): string => {
      if (depth > 64 || !isNode(node) || node.type === REF_IMAGE_NODE) return '';
      return typeof (node as { text?: unknown }).text === 'string'
        ? String((node as { text?: unknown }).text)
        : children(node).map((child) => text(child, depth + 1)).join('');
    };
    const walk = (node: unknown, scope = '', depth = 0) => {
      if (!isNode(node) || depth > 64) return;
      if (node.type === 'briefField') scope = String(node.attrs?.id ?? '');
      if (node.type === 'scriptRow') {
        result.push({ node, scope, key: JSON.stringify(children(node).map((child) => text(child))) });
        return;
      }
      children(node).forEach((child) => walk(child, scope, depth + 1));
    };
    walk(doc);
    return result;
  };
  const base = rows(masterDoc);
  const proposed = rows(suggestionDoc);
  const matches = new Map<JsonNode, JsonNode>();
  const used = new Set<JsonNode>();
  // Match pictured rows first so two text-identical rows retain their own
  // pictures when either row is deleted or moved.
  const picturedFirst = [...proposed].sort((a, b) => countRefImages(b.node) - countRefImages(a.node));
  for (const row of picturedFirst) {
    const candidates = base.filter((b) => !used.has(b.node) && b.scope === row.scope && b.key === row.key);
    const offered = new Set(collectRefImageIds(row.node));
    const source = candidates.find((b) => collectRefImageIds(b.node).some((id) => offered.has(id))) ?? candidates[0];
    if (source) { matches.set(row.node, source.node); used.add(source.node); }
  }
  for (const row of proposed) {
    if (matches.has(row.node)) continue;
    const source = base.find((b) => !used.has(b.node) && b.scope === row.scope);
    if (source) { matches.set(row.node, source.node); used.add(source.node); }
  }
  const seen = new Set<string>();
  const clean = (node: unknown, sourceRow?: JsonNode, depth = 0): unknown => {
    if (!isNode(node) || depth > 64) return node;
    if (node.type === 'scriptRow') sourceRow = matches.get(node);
    if (!Array.isArray(node.content)) return { ...node };
    const content = node.content.filter((child) => !(isNode(child) && child.type === REF_IMAGE_NODE))
      .map((child) => clean(child, sourceRow, depth + 1));
    if (node.type === 'scriptCell' && node.attrs?.kind === 'visuals' && sourceRow) {
      const sourceCell = children(sourceRow).find((c) => isNode(c) && c.type === 'scriptCell' && c.attrs?.kind === 'visuals');
      for (const image of isNode(sourceCell) ? children(sourceCell) : []) {
        if (!isNode(image) || image.type !== REF_IMAGE_NODE) continue;
        const id = imageIdOf(image);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        content.push({ ...image, attrs: { ...image.attrs } });
      }
    }
    return { ...node, content };
  };
  return clean(suggestionDoc);
}

/** String form for the routes: the same string back when nothing changed or
 *  either side can't be read (validation elsewhere owns unreadable docs). */
export function sanitizeSuggestionImagesJson(masterJson: string, suggestionJson: string): string {
  let master: unknown;
  let suggestion: unknown;
  try {
    master = JSON.parse(masterJson);
    suggestion = JSON.parse(suggestionJson);
  } catch {
    return suggestionJson;
  }
  const cleaned = JSON.stringify(sanitizeSuggestionImages(master, suggestion));
  return cleaned === JSON.stringify(suggestion) ? suggestionJson : cleaned;
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

function count(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

/** Visual cells in `doc` holding at least one of `ids`. */
function cellsHolding(doc: unknown, ids: Set<string>): number {
  let cells = 0;
  const walk = (node: unknown, depth: number) => {
    if (!isNode(node) || depth > 64) return;
    if (node.type === 'scriptCell') {
      const holds = children(node).some(
        (child) => isNode(child) && child.type === REF_IMAGE_NODE && ids.has(imageIdOf(child) ?? ''),
      );
      if (holds) cells++;
      return;
    }
    for (const child of children(node)) walk(child, depth + 1);
  };
  walk(doc, 0);
  return cells;
}

/**
 * The round summary's fixed photo bullet. The AI summary reads plain text,
 * which never includes images, so this says what changed in pictures.
 */
export function referencePhotoBullet(prevDoc: unknown, currDoc: unknown): string | null {
  const prev = new Set(collectRefImageIds(prevDoc));
  const curr = new Set(collectRefImageIds(currDoc));
  const added = new Set([...curr].filter((id) => !prev.has(id)));
  const removed = [...prev].filter((id) => !curr.has(id));
  if (added.size > 0 && removed.length > 0) return 'Updated the reference photos.';
  if (added.size > 0) {
    const shots = Math.max(1, cellsHolding(currDoc, added));
    if (shots === 1) {
      return added.size === 1
        ? 'Added a reference photo to one shot.'
        : 'Added reference photos to one shot.';
    }
    return `Added reference photos to ${count(shots)} shots.`;
  }
  if (removed.length > 0) {
    return removed.length === 1
      ? 'Removed one reference photo.'
      : `Removed ${count(removed.length)} reference photos.`;
  }
  return null;
}
